import { useEffect, useState, useCallback } from 'react';
import { request } from 'graphql-request';
import { Text, Box, VStack, Stack } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { WindowPanelProps } from '../../../types/window';
import { WindowFrame, useWindowBreakpoints } from '../../../components/window';
import { BlogPostListItem } from './parts/BlogPostListItem';
import { calculateTimeAgo } from './parts/blogDate';
import {
  BLOG_POSTS_QUERY,
  GRAPHCMS_ENDPOINT,
  type BlogPost,
  type PostsQueryResult,
} from './parts/blogTypes';
import { buildWindowsPath, openPostInList, parseOpenSegments } from '../../../routing/windows';
import RotatingCubeLoader from '../../../components/RotatingCubeLoader';
import { preloadImages } from '../../../utils/preloadImages';

import './styles.css';

/** Content width below which the list uses card layout (OS window, not viewport). */
const BLOGS_LIST_CARD_BREAKPOINT = 680;

/** List body — must render under WindowFrame so size context is available. */
function BlogsBody() {
  const [posts, setPosts] = useState<BlogPost[] | null>(null);
  const [imagesReady, setImagesReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { width, isSm } = useWindowBreakpoints();
  const navigate = useNavigate();
  const location = useLocation();

  const isCardLayout = width > 0 && width < BLOGS_LIST_CARD_BREAKPOINT;
  const padX = isSm ? 12 : 16;
  const padY = isSm ? 12 : 16;
  const isLoading = posts === null || (posts.length > 0 && !imagesReady && !loadError);

  useEffect(() => {
    let cancelled = false;

    const fetchPosts = async () => {
      try {
        setLoadError(null);
        setImagesReady(false);
        const { posts: fetchedPosts } = await request<PostsQueryResult>(
          GRAPHCMS_ENDPOINT,
          BLOG_POSTS_QUERY,
        );
        if (cancelled) return;

        const list = fetchedPosts ?? [];
        setPosts(list);

        const coverUrls = list.map((p) => p.coverPhoto?.url ?? '').filter(Boolean);
        await preloadImages(coverUrls);
        if (!cancelled) {
          setImagesReady(true);
        }
      } catch {
        if (!cancelled) {
          setLoadError('Could not load posts.');
          setPosts([]);
          setImagesReady(true);
        }
      }
    };

    void fetchPosts();
    return () => {
      cancelled = true;
    };
  }, []);

  const openPostWindow = useCallback(
    (slug: string) => {
      const next = openPostInList(parseOpenSegments(location.pathname), slug);
      navigate(buildWindowsPath(next));
    },
    [location.pathname, navigate],
  );

  return (
    <Box
      w="100%"
      h="100%"
      bg="#0F1117"
      color="white"
      overflowY="auto"
      overflowX="hidden"
      sx={{
        '::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      {isLoading ? (
        <RotatingCubeLoader />
      ) : loadError ? (
        <Stack align="center" justifyContent="center" minH="40%" spacing={4}>
          <Text fontFamily="Roboto, sans-serif" color="#f87171">
            {loadError}
          </Text>
        </Stack>
      ) : (
        <VStack
          className="blogs-list"
          spacing="16px"
          px={`${padX}px`}
          py={`${padY}px`}
          w="100%"
          maxW="100%"
          alignItems="stretch"
          boxSizing="border-box"
        >
          <Text
            fontFamily="WotfardSemi"
            fontSize={isSm ? '1rem' : '1.25rem'}
            color="#bbc1d3"
            m={0}
            px={1}
          >
            {posts.length} Blog Posts 📝
          </Text>
          {posts.map((post) => (
            <BlogPostListItem
              key={post.id}
              post={{
                id: post.id,
                title: post.title,
                description: post.description,
                readTime: post.readTime,
                datePublished: post.datePublished,
                tags: post.tags,
                coverPhotoUrl: post.coverPhoto?.url ?? '',
              }}
              isCard={isCardLayout}
              timeAgo={calculateTimeAgo(post.datePublished)}
              onOpen={() => openPostWindow(post.slug)}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
}

const Blogs = (props: WindowPanelProps) => {
  const handleClose = useCallback(() => {
    props.parentCallback(false, 'showHideBlogs');
  }, [props]);

  const handleFocus = useCallback(() => {
    props.parentCallbackTwo(0, 0, 0, 1, 0);
  }, [props]);

  return (
    <WindowFrame
      title="BLOGS"
      windowId="blogs_window"
      zIndex={props.data}
      theme="dark"
      sizePreset="lg"
      onClose={handleClose}
      onFocus={handleFocus}
      contentClassName="blogs-window-content"
    >
      <BlogsBody />
    </WindowFrame>
  );
};

export default Blogs;
